import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

interface Church {
  id: string;
  name: string;
  description: string;
  member_count: number;
  image_url?: string;
}

export default function CommunitySection() {
  const navigate = useNavigate();
  const [churches, setChurches] = useState<Church[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChurches();
  }, []);

  const fetchChurches = async () => {
    try {
      const { data, error } = await supabase
        .from('churches')
        .select('*')
        .eq('is_active', true)
        .order('member_count', { ascending: false })
        .limit(6);

      if (error) throw error;
      setChurches(data || []);
    } catch (error) {
      console.error('교회 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const getChurchImage = (church: Church, index: number) => {
    if (church.image_url) return church.image_url;
    
    // 각 교회마다 완전히 다른 실제 교회 건물 이미지 사용
    const churchImages = [
      'https://public.readdy.ai/ai/img_res/ec1b11637cb19255f0099f27ba691908.jpg',
      'https://readdy.ai/api/search-image?query=Modern%20contemporary%20church%20building%20with%20unique%20architectural%20design%20featuring%20glass%20windows%20and%20clean%20lines%20urban%20setting%20innovative%20religious%20architecture%20exterior%20facade%20daytime%20photography%20professional%20quality%20detailed%20structure&width=800&height=600&seq=church002&orientation=landscape',
      'https://readdy.ai/api/search-image?query=Historic%20stone%20church%20with%20gothic%20architecture%20featuring%20pointed%20arches%20and%20ornate%20details%20ancient%20religious%20building%20medieval%20style%20exterior%20view%20cloudy%20sky%20background%20architectural%20heritage%20high%20resolution%20detailed%20craftsmanship&width=800&height=600&seq=church003&orientation=landscape',
      'https://readdy.ai/api/search-image?query=Charming%20small%20wooden%20church%20in%20countryside%20with%20simple%20design%20and%20bell%20tower%20rural%20religious%20building%20traditional%20architecture%20surrounded%20by%20nature%20peaceful%20setting%20warm%20lighting%20exterior%20photography%20detailed%20texture&width=800&height=600&seq=church004&orientation=landscape',
      'https://readdy.ai/api/search-image?query=Large%20cathedral%20with%20impressive%20dome%20and%20classical%20architecture%20grand%20religious%20building%20baroque%20style%20exterior%20view%20majestic%20structure%20urban%20location%20blue%20sky%20professional%20photography%20high%20quality%20detailed%20design&width=800&height=600&seq=church005&orientation=landscape',
      'https://readdy.ai/api/search-image?query=Minimalist%20modern%20church%20with%20concrete%20and%20wood%20materials%20contemporary%20religious%20architecture%20simple%20geometric%20design%20exterior%20view%20natural%20surroundings%20clean%20aesthetic%20daytime%20lighting%20architectural%20photography%20detailed%20structure&width=800&height=600&seq=church006&orientation=landscape'
    ];
    
    return churchImages[index % churchImages.length];
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">교회 커뮤니티</h2>
          <p className="text-xl text-gray-600">우리 교회의 소식과 활동을 함께 나눠요</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {churches.map((church, index) => (
              <div
                key={church.id}
                onClick={() => navigate(`/community/church/${church.id}`)}
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer group"
              >
                <div className="relative w-full h-56 bg-gray-100 overflow-hidden">
                  <img
                    src={getChurchImage(church, index)}
                    alt={church.name}
                    className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-300"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = 'https://readdy.ai/api/search-image?query=Beautiful%20church%20building%20with%20traditional%20architecture%20exterior%20view%20religious%20structure%20peaceful%20atmosphere%20natural%20lighting%20high%20quality%20detailed%20design&width=800&height=600&seq=churchdefault&orientation=landscape';
                    }}
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                    {church.name}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">{church.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 text-gray-500">
                      <i className="ri-group-line"></i>
                      <span className="text-sm">{church.member_count}명</span>
                    </div>
                    <button className="text-teal-600 font-semibold hover:text-teal-700 transition-colors whitespace-nowrap">
                      자세히 보기
                      <i className="ri-arrow-right-line ml-1"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="text-center mt-12">
          <button
            onClick={() => navigate('/community')}
            className="px-8 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            모든 커뮤니티 보기
          </button>
        </div>
      </div>
    </section>
  );
}
